/**
 * NOT-542 — the two business-unit questions the estimate form has to answer,
 * kept apart because conflating them is what makes the form disagree with the
 * API.
 *
 *   • `locked`  — may the USER still touch the picker? The unit an estimate
 *     rolls up to is decided before any work is priced onto it, so the FIRST
 *     line item locks it, saved or not. An estimate spawned from a job never
 *     chooses at all: it inherits the job's unit.
 *   • `savable` — may this SAVE still state a unit? That is the server's rule,
 *     and the server reads the STORED estimate: `EstimatesService.update()`
 *     refuses a business-unit change once the persisted document has line
 *     items, and does not care what the editor is currently showing.
 *
 * Driving the payload off the editor's line count broke in both directions:
 * deleting the last line of a saved estimate re-enabled a field the API still
 * rejects (a 400 on an apparently empty estimate), while adding a line to a
 * saved empty one silently DROPPED a business-unit change the API would have
 * taken.
 */
export interface EstimateBusinessUnitGateInput {
  /** False on the New Estimate form, where there is no stored document yet. */
  isEditMode: boolean;
  /** Line items currently in the editor, including unsaved ones. */
  editorLineCount: number;
  /** Line items on the STORED estimate; undefined before it loads. */
  persistedLineCount: number | undefined;
  /** Set when the estimate was spawned from a job, which owns the unit. */
  spawnedFromJob: boolean;
}

export interface EstimateBusinessUnitGate {
  locked: boolean;
  savable: boolean;
}

export function resolveEstimateBusinessUnitGate({
  isEditMode,
  editorLineCount,
  persistedLineCount,
  spawnedFromJob,
}: EstimateBusinessUnitGateInput): EstimateBusinessUnitGate {
  const stored = persistedLineCount ?? 0;
  return {
    locked: editorLineCount > 0 || stored > 0 || spawnedFromJob,
    // A create always states the unit: the picker locks as lines are added, but
    // the choice taken while the form was empty is exactly what must be saved.
    savable: !isEditMode || stored === 0,
  };
}
